import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Trash2, ShoppingBag, ShoppingCart } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { useBasket } from "@/context/BasketContext";
import { Button } from "@/components/ui/button";
import { priceForInterval, formatIntervalPrice, isFreeProduct, symbolFor, INTERVAL_LABELS } from "@/utils/priceIntervals";

const API = getApiUrl();

/**
 * Embeddable Basket module — drop into any modular page to render the current
 * basket contents inline (rather than sending shoppers to /basket). Fully
 * config-driven via the CMS. When the basket is empty it can either hide
 * itself or show a friendly placeholder + a link back to the shop.
 *
 * Config knobs:
 *   - title         : section heading (default "Your Basket")
 *   - subtitle      : optional blurb
 *   - checkout_url  : where the Checkout CTA jumps to (default "/checkout")
 *   - continue_url  : where the "Continue shopping" link goes (default "/")
 *   - continue_label: link label (default "Continue shopping")
 *   - hide_when_empty: bool — if true, module renders nothing when basket empty
 */
export default function BasketModule({ config = {} }) {
  const {
    title = "Your Basket",
    subtitle = "",
    checkout_url = "/checkout",
    continue_url = "/",
    continue_label = "Continue shopping",
    hide_when_empty = false,
  } = config;
  const basket = useBasket();
  const [products, setProducts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch when we actually have basket contents.
    if (basket.items.length === 0) return;
    axios.get(`${API}/products`).then(({ data }) => {
      const byId = {};
      (data.products || []).forEach((p) => { byId[p.product_id] = p; });
      setProducts(byId);
    }).catch(() => {});
  }, [basket.items]);

  if (basket.count === 0 && hide_when_empty) return null;

  const lines = basket.items
    .map((i) => ({ ...i, product: products[i.product_id] }))
    .filter((l) => l.product);
  const subtotal = lines.reduce((s, l) => s + priceForInterval(l.product, l.billing_interval), 0);
  const currency = lines[0]?.product?.currency || "GBP";
  const symbol = symbolFor(currency);
  const paidInterval = (lines.find((l) => !isFreeProduct(l.product)) || lines[0])?.billing_interval;
  const intervalSuffix = paidInterval === "monthly" ? "/mo" : paidInterval === "annual" ? "/yr" : "";

  return (
    <section className="py-12 px-6" data-testid="basket-module">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-serif flex items-center gap-2"><ShoppingBag className="h-6 w-6" /> {title}</h2>
            {subtitle && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>}
          </div>
          {basket.count > 0 && (
            <button
              type="button"
              onClick={() => basket.clear()}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 shrink-0"
              data-testid="basket-module-clear-all"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear basket
            </button>
          )}
        </div>

        {basket.count === 0 ? (
          <div className="border border-dashed rounded p-8 text-center">
            <p className="text-muted-foreground mb-3">Your basket is empty.</p>
            <Link to={continue_url} className="text-primary underline text-sm">{continue_label}</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-3">
              {lines.map((l) => (
                <div key={l.product_id} className="flex gap-4 border rounded p-3 bg-background" data-testid={`basket-module-line-${l.product_id}`}>
                  {l.product.image_url
                    ? <img src={l.product.image_url} alt={l.product.name} className="h-20 w-20 object-cover rounded" />
                    : <div className="h-20 w-20 bg-muted rounded" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{l.product.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatIntervalPrice(l.product, l.billing_interval, currency)}
                      {l.billing_interval && l.billing_interval !== "one_off" && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-primary/70">
                          {INTERVAL_LABELS[l.billing_interval]}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => basket.remove(l.product_id)}
                    className="self-start text-muted-foreground hover:text-red-600"
                    title="Remove"
                    aria-label="Remove"
                    data-testid={`basket-module-remove-${l.product_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Link to={continue_url} className="inline-block text-xs text-primary underline mt-2">{continue_label}</Link>
            </div>
            <aside className="border rounded p-4 h-fit bg-background space-y-3">
              <h3 className="font-semibold">Summary</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Items ({basket.count})</span>
                <span>{symbol}{subtotal.toFixed(2)}{intervalSuffix}</span>
              </div>
              <div className="flex justify-between font-serif text-lg border-t pt-3">
                <span>Total</span>
                <span>{symbol}{subtotal.toFixed(2)}{intervalSuffix}</span>
              </div>
              <Button
                className="w-full"
                onClick={() => navigate(checkout_url)}
                data-testid="basket-module-checkout"
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                Proceed to checkout
              </Button>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}
